<template>
	<div class="pl8 pr8">
		<!-- 2 columns -->
		<j-spinner v-if="isLoading" class="p15" size="medium" :delay="0" />
		<div v-else class="flex wrap xs-12">
			<!-- Left -->
			<div v-if="isEditable" class="xs-12 md-6 md-pr4">
				<j-card class="bg5">
					<!-- Headline -->
					<div class="xs-12 p4 bb1 bc7">
						<div class="fs5 fw7 mb1">{{ $t("current_policy") }}</div>
						<div class="lh125">
							{{ $t("this_data_represents_current_policy") }}
						</div>
					</div>

					<!-- Body -->
					<div class="p2">
						<!-- Endorsement Handler -->
						<endorsement-handler
							endorseType="policy"
							v-model="endorsement"
							:endorsementId="endorsementId"
							:policyId="id"
							:policy="policy"
							:unsavedEndorsements="unsavedEndorsements"
							:isEditable="isEditable"
							@endorse:remove="onChangeEndorsement('remove')"
						>
							<div
								slot-scope="{
									policySection,
									holderSection,
									productSection,
									insuredListSection,
									otherHoldersSection,
									premiumSection,
									clausesSection,
									pricingSection,
									bindersSection,
									benefitOptionsSection
								}"
							>
								<section-policy
									:componentProps="policySection.componentProps"
									:componentHandlers="policySection.componentHandlers"
									v-on="policySection.sectionHandlers"
								/>

								<section-product
									:componentProps="productSection.componentProps"
									:componentHandlers="productSection.componentHandlers"
									v-on="productSection.sectionHandlers"
									:isEditable="
										!['aeg', 'aeg_uat'].includes($store.state.tenantId)
									"
								/>

								<!-- MOCK cms : Pricing for each AEG product-->
								<!-- We reuse the mocked components from case management, so the API calls are made directly inside the modal for pricing -->
								<j-card
									class="mb2 bg5"
									v-if="
										($store.state.tenantId === 'aeg' ||
											$store.state.tenantId === 'aeg_uat') &&
											policy.product
									"
								>
									<j-accordion colorWhenClosed="bg0" class="cu1">
										<div slot="title" class="fs4 fw7">Pricing</div>
										<!-- Non-editable display -->
										<aeg-pricing
											slot="body"
											:productId="policy.product.productId"
											:policyId="id"
											:clauses="policy.clauses"
											:rawValues="policy.values"
											:contractInsured="policy.contractInsured"
											:isEditable="false"
											:isPremiumOverriden="policy.isPremiumOverriden"
										/>
										<!-- Button to open the editable pricing in the modal -->
										<div slot="body" class="flex xs-right p4">
											<j-button
												type="primary"
												size="small"
												v-on="pricingSection.sectionHandlers"
												>{{ $t("update") }}</j-button
											>
										</div>
									</j-accordion>
								</j-card>

								<section-premium
									:componentProps="premiumSection.componentProps"
									:componentHandlers="premiumSection.componentHandlers"
								/>

								<section-clauses
									:componentProps="clausesSection.componentProps"
									:componentHandlers="clausesSection.componentHandlers"
								/>
								<section-holder
									v-if="holderSection"
									:componentProps="holderSection.componentProps"
									:componentHandlers="holderSection.componentHandlers"
									v-on="holderSection.sectionHandlers"
								/>
								<div class="flex xs-right xs-12">
									<j-button
										type="tertiary"
										class="mr4 mt2"
										@click="
											openAddEntity(
												'New Other Holder',
												'addOtherContractHolder',
												[
													{ name: 'Individual', value: 'individual' },
													{ name: 'Company', value: 'company' }
												]
											)
										"
										>{{ "+" + $t("add_other_holder") }}</j-button
									>
								</div>
								<section-other-holders
									:otherHoldersSection="otherHoldersSection"
								/>
								<div class="flex xs-right xs-12">
									<j-button
										type="tertiary"
										class="mr4 mt2"
										@click="
											openAddEntity('New Insured', 'addContractInsured', [
												{ name: 'Individual', value: 'individual' },
												{ name: 'Company', value: 'company' },
												{ name: 'Object', value: 'object' }
											])
										"
										>{{ "+" + $t("add_insured") }}</j-button
									>
								</div>
								<section-insured
									:insuredListSection="insuredListSection"
									@item:remove="onChangeEndorsement('remove')"
								/>
								<section-binders
									v-bind="bindersSection.componentProps"
									v-on="bindersSection.sectionHandlers"
								/>
								<!-- Benefit Options -->
								<section-benefit-options
									v-if="!['aeg', 'aeg_uat'].includes($store.state.tenantId)"
									v-bind="benefitOptionsSection.componentProps"
									v-on="benefitOptionsSection.sectionHandlers"
									:showButtons="true"
									class="bg5"
								/>
							</div>
						</endorsement-handler>
					</div>
				</j-card>
			</div>

			<!-- Right -->
			<div :class="isEditable ? 'xs-12 md-6 md-pl4 pt4 md-pt0' : 'xs-12 pl4'">
				<j-card class="bg5">
					<!-- Headline -->
					<div class="xs-12 p4 bb1 bc7">
						<div class="fs5 fw7 mb1">{{ $t("endorsement") }}</div>
						<div class="pt2 pb2">
							<j-tag
								:label="endorsementStatus"
								:variant="decideStatusClass(endorsementStatus)"
							/>
						</div>
						<div class="lh125">
							{{ $t("this_represents_endorsement_document") }}
						</div>
					</div>

					<!-- Body -->
					<div class="p2">
						<data-list
							v-if="unsavedEndorsements && unsavedEndorsements.length"
							:list="unsavedEndorsements"
							:key="'list-endorsements'"
						>
							<!-- @item:remove="removeFromEndorsement($event)" -->
							<template #list-item="{item}">
								<div class="flex xs-column xs-12">
									<div class="flex xs-row xs-between xs-top">
										<!-- <h5 class="fw7 fs4 c6 lh125 mb2">{{ $t(item.label) }}</h5> -->
										<!-- <div> -->
										<js-object-view
											:data="item.value"
											:maxDepth="0"
											:rootKey="$t(item.label)"
										></js-object-view>
										<div v-if="isEditable">
											<j-button
												v-for="(action, i) in item.actions"
												:key="'action' + i"
												type="tertiary"
												size="medium"
												class="ml2"
												@click="handleUnsavedEndorsementsAction(action, item)"
											>
												<!-- @click="$emit(`item:${action}`, item)" -->
												<crm-icon
													v-if="action === 'remove'"
													name="delete"
													size="small"
													color="cError"
												/>
												<crm-icon v-else name="edit" size="small" color="c2" />
											</j-button>
										</div>
										<!-- </div> -->
									</div>
									<!-- <h5 class="pl4">{{ JSON.stringify(item.value) }}</h5> -->
									<div class="flex xs-row xs-between fw3 fs3 c1 lh125 m2 mb0">
										<span>
											{{
												new Intl.DateTimeFormat(
													$store.state.settings.locale || "en",
													{
														year: "numeric",
														month: "2-digit",
														day: "2-digit",
														hour: "2-digit",
														minute: "2-digit",
														second: "2-digit",
														timeZoneName: "long"
													}
												).format(new Date(item.timestamp))
											}}
										</span>
										<span>{{ getAddedBy(item) }}</span>
									</div>
								</div>
							</template>
						</data-list>
					</div>

					<!-- Actions -->
					<div v-if="isEditable" class="flex xs-12 xs-between p4 mt8 bc7 bt1">
						<div class="flex xs-between xs-12 p2">
							<v-popover
								class="xs-12 flex xs-right"
								placement="bottom-end"
								popoverClass="xs-12 r2"
								popoverArrowClass="xs-0"
								popoverInnerClass="xs-12 r2 bg5 b1 bc7"
							>
								<j-button type="bordered" size="xsmall">
									{{ $t("actions") }}
									<crm-icon name="down" color="c2" size="small" />
								</j-button>

								<transition slot="popover" name="fade" :duration="160">
									<div class="bt1 bc7">
										<template>
											<div
												class="xs-12 bb1 bc7 p3 fw7 cu2 c1"
												@click="approve()"
												v-close-popover
											>
												{{ $t("approve") }}
											</div>
											<div
												class="xs-12 bb1 bc7 p3 fw7 cu2 cError"
												@click="discard()"
												v-close-popover
											>
												{{ $t("reject") }}
											</div>
										</template>
									</div>
								</transition>
							</v-popover>
						</div>
					</div>
				</j-card>
			</div>
		</div>
		<!-- Modals -->
		<core-modal
			name="modalEditor"
			:width="endorsement.activeEndorsement.modalWidth"
			:headline="endorsement.activeEndorsement.label"
			:key="endorsement.activeEndorsement.label"
		>
			<div :class="{ p4: !endorsement.activeEndorsement.noPadding }">
				<div v-if="!endorsement.activeEndorsement.hideLabel" class="fw7 mb1">
					{{ endorsement.activeEndorsement.label }}
				</div>
				<div>
					<component
						:is="endorsement.activeEndorsement.component"
						v-model="endorsement.activeEndorsement.value"
						v-bind="endorsement.activeEndorsement.componentProps"
						@showButtons="endorsement.activeEndorsement.hideButtons = false"
						@pricing:updated="onSavePricing"
						@benefit:saved="onSaveBenefitOption"
					/>
					<!-- Add empty component under, used for components that need to show options in multi select (e.g : CoreSelectBinders, CoreSelectProduct...)  -->
					<div
						v-if="endorsement.activeEndorsement.whiteSpaceUnder"
						class="xs-12 pt15 pb15 mb5"
					/>
				</div>
				<div
					v-if="!endorsement.activeEndorsement.hideButtons"
					class="flex xs-12 xs-between mt6"
					:class="{ 'pb4 pl4 pr4': endorsement.activeEndorsement.noPadding }"
				>
					<j-button
						v-if="endorsement.activeEndorsement.type === 'add'"
						:isLoading="isLoading"
						@click="onChangeEndorsement('add')"
						>{{ $t("add") }}</j-button
					>
					<j-button
						v-else
						:isLoading="isLoading"
						@click="onChangeEndorsement('update')"
						>{{ $t("update") }}</j-button
					>
					<j-button @click="$modal.hide('modalEditor')" type="bordered">
						{{ $t("cancel") }}
					</j-button>
				</div>
			</div>
		</core-modal>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JSpinner from "@/components/JSpinner/"
import JBaseToggle from "@/components/JBaseToggle/"
import JCard from "@/components/JCard/"
import PolicySubmenu from "./PolicySubmenu"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreSelectBinders from "@/modules/core/components/CoreSelectBinders.vue"
import CoreSelectBenefitOption from "@/modules/core/components/CoreSelectBenefitOption"
import CoreSelectProduct from "@/modules/core/components/CoreSelectProduct"
import JList from "@/components/JList"
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputLongText from "@/components/JBaseInputLongText"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import JBaseDatePickerSimple from "@/components/JBaseDatePickerSimple"
import DataList from "./DataList"
import JAccordion from "@/components/JAccordion"
import AdjustGrossPremium from "./AdjustGrossPremium"
import AddUpdateDiscount from "./AddUpdateDiscount"
import AddLoadings from "./CurrentPolicyDisplay/SectionPremium/AddLoadings"
import InputNewContact from "./InputModalComponents/InputNewContact"
import InputNewFact from "./InputModalComponents/InputNewFact"
import InputNewAddress from "./InputModalComponents/InputNewAddress"
import InputNewIdentity from "./InputModalComponents/InputNewIdentity"
import InputNatureOfBusiness from "./InputModalComponents/InputNatureOfBusiness"
import UpdateAddressInput from "./InputModalComponents/UpdateAddressInput"
import AddNewEntity from "./InputModalComponents/AddNewEntity"
import JsObjectView from "./JsObjectView"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import JTag from "@/components/JTag/"

import ClausesUpdate from "./CurrentPolicyDisplay/SectionClauses/ClausesUpdate"
import ClausesAdd from "./CurrentPolicyDisplay/SectionClauses/ClausesAdd"
import SectionPolicy from "./CurrentPolicyDisplay/SectionPolicy"
import SectionProduct from "./CurrentPolicyDisplay/SectionProduct"
import SectionHolder from "./CurrentPolicyDisplay/SectionHolder"
import SectionPremium from "./CurrentPolicyDisplay/SectionPremium"
import SectionClauses from "./CurrentPolicyDisplay/SectionClauses"
import SectionOtherHolders from "./CurrentPolicyDisplay/SectionOtherHolders"
import SectionInsured from "./CurrentPolicyDisplay/SectionInsured"
import SectionBinders from "./CurrentPolicyDisplay/SectionBinders"
import SectionBenefitOptions from "./CurrentPolicyDisplay/SectionBenefitOptions"
import EndorsementHandler from "./CurrentPolicyDisplay/EndorsementHandler"
import AegPricing from "./mockedCms/AegPricing"

// HACK: brinnging component from different module here
// We might establish a rule that caseManagement and policies can share some components across
import ProposalPremiumInfo from "@/modules/caseManagement/components/PremiumInfo/ProposalPremiumInfo"

import "core-js/fn/array/flat-map"

// Helper to replace Object.fromEntries
// The version of core-js we use does not have it, so babel is unable to polyfill for IE11
const fromEntries = function(entries) {
	return [...entries].reduce((obj, [key, val]) => {
		obj[key] = val
		return obj
	}, {})
}

export default {
	name: "EndorsementDetails",

	components: {
		CrmIcon,
		JButton,
		JCard,
		PolicySubmenu,
		CoreModal,
		CoreSelectBinders,
		CoreSelectProduct,
		JList,
		JBaseInputText,
		JBaseInputLongText,
		DataList,
		JAccordion,
		ProposalPremiumInfo,
		JBaseDatePickerSimple,
		JSpinner,
		AdjustGrossPremium,
		AddUpdateDiscount,
		JBaseToggle,
		JBaseInputNumber,
		UpdateAddressInput,
		InputNewContact,
		InputNewFact,
		InputNewAddress,
		InputNewIdentity,
		InputNatureOfBusiness,
		AddNewEntity,
		JsObjectView,
		JTag,
		ClausesUpdate,
		ClausesAdd,
		SectionPolicy,
		SectionProduct,
		SectionPremium,
		SectionHolder,
		SectionClauses,
		SectionOtherHolders,
		SectionInsured,
		SectionBinders,
		SectionBenefitOptions,
		CoreSelectBenefitOption,
		EndorsementHandler,
		AegPricing,
		AddLoadings
	},

	props: {
		id: String, // policyId
		endorsementId: String
	},

	data() {
		return {
			unsavedLinkedEntities: {},
			isLoading: true,
			isActionsPopoverExpanded: false,
			policy: {},
			endorsement: {
				activeEndorsement: {
					component: "JBaseInputText"
				},
				endorsed: []
			}
		}
	},

	watch: {
		unsavedLinkedEntitiesIds: {
			handler: function(newEntities) {
				if (newEntities) {
					newEntities.forEach(async entityId => {
						if (!this.unsavedLinkedEntities[entityId]) {
							const newEntity = await this.fetchEntity(entityId)
							if (newEntity) {
								this.unsavedLinkedEntities = {
									...this.unsavedLinkedEntities,
									[entityId]: newEntity
								}
							}
						}
					})
				}
			},
			immediate: true
		}
	},

	computed: {
		graphQlQuery() {
			return this.selectedTemplate?.[0]?.graphQlQuery
		},
		isEditable() {
			const endorsement = this?.policy?.endorsements?.find(
				({ id }) => id === this.endorsementId
			)
			return !(endorsement?.isApproved || endorsement?.isRejected)
		},
		endorsementStatus() {
			return this?.policy?.endorsements?.find(
				({ id }) => id === this.endorsementId
			).status
		},
		unsavedLinkedEntitiesIds() {
			if (!this.unsavedEndorsements?.length) return []
			return this.unsavedEndorsements
				.filter(endorsement => endorsement.value.entityId)
				.map(endorsement => endorsement.value.entityId)
		},
		unsavedEndorsements() {
			/**
			 * HELPER FUNCTIONS
			 */
			const pipeFunctions = (...fns) =>
				fns.reduce((f, g) => (...args) => g(f(...args)))

			const isObject = input =>
				input === Object(input) &&
				Object.prototype.toString.call(input) !== "[object Array]"

			const camelCaseToSnakeCase = str =>
				str
					.split(/(?=[A-Z])/)
					.join("_")
					.toLowerCase()

			function scalarValueToJsObject({
				arrayValue,
				booleanValue,
				dateValue,
				numberValue,
				objectValue,
				stringValue
			}) {
				if (
					!arrayValue &&
					!(booleanValue === false || booleanValue === true) &&
					!dateValue &&
					!numberValue &&
					!objectValue &&
					!stringValue
				)
					return
				console.log(
					"creating boolean",
					typeof booleanValue === "boolean" && Boolean(booleanValue)
				)
				return (
					(stringValue && stringValue.toString()) ||
					numberValue ||
					(dateValue && new Date(dateValue)) ||
					(arrayValue &&
						arrayValue.length &&
						arrayValue.map(sv => sv && scalarValueToJsObject(sv))) ||
					(objectValue &&
						fromEntries(
							objectValue.map(({ key, value }) => [
								key,
								scalarValueToJsObject(value)
							])
						)) ||
					(typeof booleanValue === "boolean" ? Boolean(booleanValue) : null)
				)
			}

			/**
			 * Atomic Logic functions
			 */
			const filterOutUnchanged = entries =>
				entries.filter(([key, value], index, array) =>
					array.some(
						([k, v]) =>
							k === `is${key.charAt(0).toUpperCase() + key.slice(1)}Changed` &&
							v === true
					)
				)

			const filterInCurrentEndorsement = endorsements =>
				endorsements.filter(({ id }) => id === this.endorsementId)

			const flattenToCommandTypes = endorsements =>
				endorsements.flatMap(
					({
						id: endorsementId,
						status: endorsementStatus,
						isApproved: endorsementIsApproved,
						isRejected: endorsementIsRejected,
						...endorsement
					}) =>
						Object.entries(endorsement).map(([command, changeObj]) => ({
							command,
							changeObj,
							endorsementStatus,
							isApproved: endorsementIsApproved,
							isRejected: endorsementIsRejected,
							endorsementId
						}))
				)

			const filterOutUnusedCommandTypes = commands =>
				commands.filter(({ changeObj }) => !!changeObj)

			const flattenToCommands = commandType =>
				commandType.flatMap(
					({ command, changeObj, endorsementStatus, endorsementId }) =>
						changeObj.map(changeObj => ({
							...changeObj,
							command,
							endorsementStatus,
							endorsementId
						}))
				)

			const makeKeysSnakeCase = entries =>
				entries.map(([k, v]) => [camelCaseToSnakeCase(k), v])

			const translateKeys = entries => entries.map(([k, v]) => [this.$t(k), v])

			const getChangesFromPatchObj = pipeFunctions(
				Object.entries,
				filterOutUnchanged,
				recurseGetChangesFnIfValueIsObject,
				makeKeysSnakeCase,
				translateKeys,
				fromEntries
			)

			const getValueByCommandType = (changeObj, command) => {
				/**
				 * Handle special cases first
				 */
				console.log("changeObj", changeObj)
				if (changeObj.entityId) {
					if (
						[
							"removeContractInsuredAddressCommands",
							"removeContractInsuredFactCommands"
						].includes(command)
					) {
						const fieldMap = {
							removeContractInsuredAddressCommands: "addresses",
							removeContractInsuredFactCommands: "facts"
						}
						const locator = fieldMap[command]
						return this.unsavedLinkedEntities &&
							this.unsavedLinkedEntities[changeObj.entityId]
							? {
									[this.unsavedLinkedEntities[changeObj.entityId].name]: {
										type: this.unsavedLinkedEntities[changeObj.entityId][
											locator
										].find(({ id }) => id === changeObj.primitiveId)?.type
									}
							  }
							: changeObj
					}

					if (
						[
							"addOtherContractHolderContactCommands",
							"addOtherContractHolderIdentityCommands",
							"addContractInsuredContactCommands",
							"addContractInsuredIdentityCommands",
							"updateContractInsuredContactCommands"
						].includes(command)
					) {
						return this.unsavedLinkedEntities &&
							this.unsavedLinkedEntities[changeObj.entityId] &&
							changeObj.type &&
							changeObj.value
							? {
									[this.unsavedLinkedEntities[changeObj.entityId].name]: {
										[changeObj.type]: changeObj.value
									}
							  }
							: changeObj
					}

					if (
						[
							"addOtherContractHolderAddressCommands",
							"addContractInsuredAddressCommands"
						].includes(command)
					) {
						return this.unsavedLinkedEntities &&
							this.unsavedLinkedEntities[changeObj.entityId]
							? {
									[this.unsavedLinkedEntities[changeObj.entityId].name]: {
										[changeObj.type]: changeObj.fields.reduce(
											(address, { type, value }) => {
												return {
													...address,
													[type]: value
												}
											},
											{}
										)
									}
							  }
							: {
									[changeObj.type]: changeObj.fields.reduce(
										(address, { type, value }) => {
											return {
												...address,
												[type]: value
											}
										},
										{}
									)
							  }
					}

					if (
						[
							"addOtherContractHolderFactCommands",
							"updateContractInsuredFactCommands"
						].includes(command)
					) {
						return this.unsavedLinkedEntities &&
							this.unsavedLinkedEntities[changeObj.entityId]
							? {
									[this.unsavedLinkedEntities[changeObj.entityId].name]: {
										[changeObj.type]: changeObj.value
											? scalarValueToJsObject(changeObj.value)
											: null
									}
							  }
							: {
									[changeObj.type]: changeObj.value
										? scalarValueToJsObject(changeObj.value)
										: null
							  }
					}

					return (
						(this.unsavedLinkedEntities &&
							this.unsavedLinkedEntities[changeObj.entityId]) ||
						changeObj
					)
				}

				// Handle add AssociatedContracts to get only binder number
				if (["addAssociatedContractCommands"].includes(command)) {
					return {
						[this.$t("binder_number")]: changeObj?.contract?.facts?.find(
							fact => fact.type === "binderNumber"
						)?.value?.stringValue
					}
				}

				/**
				 * Handle ADD insured section (fetch entity)
				 */
				if (
					[
						"addContractInsuredCommands",
						"removeContractInsuredCommands",
						"addOtherContractHolderCommands",
						"removeOtherContractHolderCommands"
					].includes(command)
				) {
					const fullEntity =
						this.unsavedLinkedEntities &&
						this.unsavedLinkedEntities[changeObj.entityId]
					return {
						...changeObj,
						...fullEntity
					}
				}

				/**
				 * Handle Templates from template clauses
				 */

				if (command === "addClauseCommands") {
					return {
						order: changeObj.order,
						clause: changeObj.renderedHtmlResult.value
					}
				}

				/**
				 * Handle commands that return scalar values in the value property
				 */
				if (
					[
						"updatePolicyFactCommands",
						"updateContractHolderFactCommands"
					].includes(command)
				) {
					return {
						[changeObj.type]: changeObj.value
							? scalarValueToJsObject(changeObj.value)
							: null
					}
				}

				if (["upsertBenefitOptionCommands"].includes(command)) {
					return {
						[changeObj.typeId]: changeObj.value
							? scalarValueToJsObject(changeObj.value)
							: null
					}
				}

				/**
				 * Address Commands
				 */
				if (["addContractHolderAddressCommands"].includes(command)) {
					return {
						[changeObj.type]: changeObj.fields.reduce(
							(address, { type, value }) => {
								return {
									...address,
									[type]: value
								}
							},
							{}
						)
					}
				}

				/**
				 * Contact && Identity Commands
				 */
				if (
					[
						"addContractHolderContactCommands",
						"updateContractHolderContactCommands",
						"addContractHolderIdentityCommands",
						"updateContractHolderIdentityCommands"
					].includes(command)
				) {
					return changeObj.type && changeObj.value
						? { [changeObj.type]: changeObj.value }
						: changeObj
				}

				/**
				 * Handle general ADD command values
				 */
				if (
					[
						"addClauseCommands",
						"addContractHolderAddressCommands",
						"addContractHolderContactCommands",
						"addContractHolderFactCommands",
						"addContractHolderIdentityCommands",
						"addContractInsuredAddressCommands",
						"addContractInsuredContactCommands",
						"addContractInsuredFactCommands",
						"addContractInsuredIdentityCommands",
						"addOtherContractHolderAddressCommands",
						"addOtherContractHolderContactCommands",
						"addOtherContractHolderFactCommands",
						"addOtherContractHolderIdentityCommands",
						"addPolicyFactCommands"
					].includes(command)
				) {
					return {
						[changeObj.type]: changeObj.value
							? scalarValueToJsObject(changeObj.value)
							: null
					}
				}

				/**
				 * Handle adding, updating and removing discounts
				 */
				if (
					[
						"addDiscountCommands",
						"updateDiscountCommands",
						"removeDiscountCommands"
					].includes(command)
				) {
					let discount
					try {
						const parsedDiscountLogic = JSON.parse(
							changeObj.calculationJsonLogic
						)
						const flatOrRatio = parsedDiscountLogic?.["-"] ? "flat" : "ratio"
						const discount_amount =
							flatOrRatio === "flat"
								? parsedDiscountLogic?.["-"]?.[1]
								: `- ${100 - parsedDiscountLogic?.["*"]?.[1] * 100}%`
						discount = {
							[flatOrRatio]: {
								discount_amount,
								order: changeObj.order || 0
							}
						}
					} catch (error) {
						discount = {
							flat: { discount_amount: 0, order: changeObj.order || 0 }
						}
					}
					return discount
				}

				/**
				 * Handle add loading
				 */
				if ("addLoadingCommands" === command) {
					return {
						order: changeObj.order,
						...(changeObj.flat && { flat: changeObj.flat }),
						...(changeObj.ratio && { flat: changeObj.ratio })
					}
				}

				/**
				 * Handle general PUT command values
				 */
				if (
					[
						"updateContractHolderAddressCommands",
						"updateContractHolderIdentityCommands",
						"updateContractInsuredAddressCommands",
						"updateContractInsuredContactCommands",
						"updateContractInsuredIdentityCommands",
						"updateOtherContractHolderAddressCommands",
						"updateOtherContractHolderContactCommands",
						"updateOtherContractHolderIdentityCommands",
						"addDiscountCommands"
					].includes(command)
				) {
					return changeObj.type && changeObj.value
						? { [changeObj.type]: changeObj.value }
						: changeObj
				}

				/**
				 * Handle general Remove command values
				 */
				if (
					[
						"removeOtherContractHolderCommands",
						"removeContractInsuredCommands",
						"removeContractHolderAddressCommands",
						"removeContractHolderIdentityCommands",
						"removeContractHolderContactCommands",
						"removeContractHolderFactCommands",
						"removeContractInsuredContactCommands",
						"removeContractInsuredAddressCommands",
						"removeContractInsuredIdentityCommands",
						"removeContractInsuredFactCommands",
						"removeOtherContractHolderAddressCommands",
						"removeOtherContractHolderIdentityCommands",
						"removeOtherContractHolderContactCommands",
						"removeOtherContractHolderFactCommands",
						"removePolicyFactCommands",
						"removeAssociatedContractCommands",
						"removeBenefitOptionCommands",
						"removeLoadingCommands"
					].includes(command)
				) {
					return changeObj
				}

				/**
				 * Handle general Patch command values
				 */
				if (
					[
						"updateContractHolderFactCommands",
						"updateContractInsuredCompanyCommands",
						"updateContractInsuredFactCommands",
						"updateContractInsuredIndividualCommands",
						"updateContractInsuredObjectCommands",
						"updateDiscountCommands",
						"updateOtherContractHolderCompanyCommands",
						"updateOtherContractHolderFactCommands",
						"updateOtherContractHolderIndividualCommands",
						"updatePolicyCommands",
						"updatePolicyFactCommands"
					].includes(command)
				) {
					return getChangesFromPatchObj(changeObj)
				}
				throw new Error(
					`Commmand: '${command}' not handled in getEndorsementCommands`
				)
			}
			const handleCommands = commands =>
				commands.map(
					({
						timestamp,
						addedBy,
						modifiedBy,
						removedBy,
						upsertedBy,
						commandId,
						command,
						endorsementStatus,
						endorsementId,
						endorsementIsApproved,
						endorsementIsRejected,
						...changeObj
					}) => {
						const value = getValueByCommandType(changeObj, command)

						return {
							timestamp,
							addedBy,
							modifiedBy,
							removedBy,
							upsertedBy,
							commandId,
							actions: ["remove"],
							label: command,
							endorsementStatus,
							endorsementIsApproved,
							endorsementIsRejected,
							endorsementId,
							value
						}
					}
				)

			const sortByDateAsc = commandHistory =>
				commandHistory.sort(({ timestamp: a }, { timestamp: b }) => {
					if (a < b) return -1
					if (a > b) return 1
					return 0
				})

			function recurseGetChangesFnIfValueIsObject(entries) {
				return entries.map(([k, v]) => [
					k,
					isObject(v) ? getChangesFromPatchObj(v) : v
				])
			}
			const handleEndorsements = pipeFunctions(
				filterInCurrentEndorsement,
				flattenToCommandTypes,
				filterOutUnusedCommandTypes,
				flattenToCommands,
				handleCommands,
				sortByDateAsc
			)
			return (
				this.policy?.endorsements &&
				handleEndorsements(this.policy.endorsements)
			)
		}
	},

	methods: {
		onSaveBenefitOption(option) {
			this.$modal.hide("modalEditor")
			this.endorsement.activeEndorsement.value = option
			this.onChangeEndorsement(this.endorsement.activeEndorsement.type)
		},

		decideStatusClass(status) {
			if (status === "APPROVED") return "success"
			if (status === "REJECTED") return "error"
			else return "neutral"
		},
		getAddedBy({ addedBy, modifiedBy, removedBy, upsertedBy }) {
			return (
				addedBy?.associatedUser?.name ||
				addedBy?.username ||
				addedBy?.email ||
				modifiedBy?.associatedUser?.name ||
				modifiedBy?.username ||
				modifiedBy?.email ||
				removedBy?.associatedUser?.name ||
				removedBy?.username ||
				removedBy?.email ||
				upsertedBy?.associatedUser?.name ||
				upsertedBy?.username ||
				upsertedBy?.email
			)
		},
		openAddEntity(label, addCall, options) {
			this.endorsement.activeEndorsement = {
				label: label,
				component: "AddNewEntity",
				componentProps: { createOptions: options },
				hideButtons: true,
				hideLabel: true,
				noPadding: true,
				type: "add",
				add: async input => {
					const query = gql`
							mutation ${addCall}(
								$policyId: String!
								$endorsementId: String!
								$entityId: String!
							) {
								${addCall}(
									policyId: $policyId
									endorsementId: $endorsementId
									entityId: $entityId
								) {
									createdStatus {
										id
									}
									errors
									status
								}
							}
					`
					const variables = {
						endorsementId: this.endorsementId,
						entityId: input.id,
						policyId: this.id
					}
					const res = await api({ query, variables })
					if (res?.data?.[addCall]?.createdStatus?.id) {
						return { status: "success", id: res.data[addCall].createdStatus.id }
					} else {
						this.$store.dispatch("addMessage", {
							message: {
								type: "error",
								text: res?.errors
									? res?.errors
									: res?.data?.[addCall]?.errors
									? res?.data?.[addCall]?.errors
									: res?.data?.[addCall]?.errors_2.message
									? res?.data?.[addCall]?.errors_2.message
									: "Oops ! An error occured."
							}
						})
						return { status: "error", id: "" }
					}
				}
			}
			this.$nextTick().then(() => {
				this.$modal.show("modalEditor")
			})
		},
		async discard() {
			const query = gql`
				mutation rejectEndorsement(
					$policyId: String!
					$endorsementId: String!
				) {
					rejectEndorsement(
						policyId: $policyId
						endorsementId: $endorsementId
					) {
						errors
						status
						errors_2 {
							code
							message
						}
					}
				}
			`
			const variables = { policyId: this.id, endorsementId: this.endorsementId }
			const res = await api({ query, variables })
			if (res?.data?.rejectEndorsement?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res?.data?.rejectEndorsement?.errors
							? res.data.rejectEndorsement.errors
							: res.data.rejectEndorsement.errors_2.message
							? res.data.rejectEndorsement.errors_2.message
							: "Unable to remove endorsement."
					}
				})
			} else {
				this.$router.push({
					name: "Endorsements",
					params: { id: this.id }
				})
			}
		},
		async approve() {
			const query = gql`
				mutation acceptEndorsement(
					$policyId: String!
					$endorsementId: String!
				) {
					acceptEndorsement(
						policyId: $policyId
						endorsementId: $endorsementId
					) {
						errors
						status
						errors_2 {
							code
							message
						}
					}
				}
			`
			const variables = { policyId: this.id, endorsementId: this.endorsementId }
			const res = await api({ query, variables })
			if (res?.data?.acceptEndorsement?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res?.data?.acceptEndorsement?.errors
							? res.data.acceptEndorsement.errors
							: res.data.acceptEndorsement.errors_2.message
							? res.data.acceptEndorsement.errors_2.message
							: "Unable to accept endorsement."
					}
				})
			} else {
				this.$router.push({
					name: "PolicyDetail",
					params: { id: this.id }
				})
			}
		},

		async onChangeEndorsement(changeType) {
			this.isLoading = true
			// Call API
			const res =
				changeType === "update"
					? await this.endorsement.activeEndorsement.update(
							this.endorsement.activeEndorsement.value
					  )
					: changeType === "remove"
					? await this.endorsement.activeEndorsement.remove(
							this.endorsement.activeEndorsement.value
					  )
					: await this.endorsement.activeEndorsement.add(
							this.endorsement.activeEndorsement.value
					  )
			this.isLoading = false
			// Check status. If success, save it
			if (res?.status === "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: "Changes on successfully added to the current endorsement."
					}
				})
				this.$modal.hide("modalEditor")
				this.fetchPolicy()
			}
		},

		// Need different function for pricing because the API calls is made inside the modal components
		onSavePricing() {
			this.$modal.hide("modalEditor")
			this.fetchPolicy()
		},

		handleUnsavedEndorsementsAction(action, item) {
			const actions = {
				remove: this.removeFromEndorsement
			}
			return actions[action](item)
		},

		async removeFromEndorsement(item) {
			// const removeMap = {
			// 	updatePolicyCommands: "function"
			// }

			// removeMap
			// Call API
			const policyId = this.id
			const endorsementId = this.endorsementId
			const commandId = item.commandId
			const query = gql`
				mutation RemoveCommandFromEndorsement(
					$policyId: String!
					$endorsementId: String!
					$commandId: String!
				) {
					removeCommandFromEndorsement(
						policyId: $policyId
						endorsementId: $endorsementId
						commandId: $commandId
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				policyId,
				endorsementId,
				commandId
			}
			const {
				errors: rootErrors,
				data: {
					removeCommandFromEndorsement: { errors, errors_2, status } = {}
				} = {}
			} = await api({ query, variables })

			// Check status. If success, save it
			if (status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							errors_2?.message ||
							errors ||
							(rootErrors &&
								rootErrors.length &&
								rootErrors.map(e => e.message))
					}
				})
			}
			this.fetchPolicy()
		},

		// Try to fetch entity as indibidual, then as company, then as object. Returns entity when get one
		async fetchEntity(entityId) {
			let newEntity = await this.fetchEntityByType("individuals", entityId)
			if (!newEntity)
				newEntity = await this.fetchEntityByType("companies", entityId)
			if (!newEntity)
				newEntity = await this.fetchEntityByType("objects", entityId)
			if (newEntity) return newEntity
		},

		async fetchEntityByType(entityType, entityId) {
			// Individual query
			const individualQuery = gql`
				query individuals($where: individualWhereInput) {
					individuals(where: $where) {
						list {
							id
							name
							englishFirstName
							englishLastName
							chineseFirstName
							chineseLastName
							dateOfBirth
							gender
							occupation
							maritalStatus
							employmentStatus
							salutation
							preferredCommunicationChannel
							preferredLanguage
							countryOfResidency
							incomeRange {
								exclusiveMaximum
								exclusiveMinimum
								minimum
								maximum
							}
							acceptsMarketing
							source
							internalCode
							contacts {
								id
								type
								value
							}
							identities {
								id
								type
								value
							}
							addresses {
								id
								fields2 {
									type
									value
								}
								fields
								type
							}
							facts {
								id
								type
								value {
									booleanValue
									dateValue
									numberValue
									stringValue
									arrayValue {
										stringValue
										booleanValue
										dateValue
										numberValue
										objectValue {
											key
											value {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
										arrayValue {
											stringValue
											booleanValue
											dateValue
											numberValue
											arrayValue {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
									}
								}
							}
						}
					}
				}
			`
			const companyQuery = gql`
				query companies($where: companyWhereInput) {
					companies(where: $where) {
						list {
							id
							name
							registrationNumber
							natureOfBusiness
							acceptsMarketing
							photoPath
							source
							contacts {
								id
								type
								value
							}
							addresses {
								id
								fields2 {
									type
									value
								}
								fields
								type
							}
							identities {
								id
								type
								value
							}
							facts {
								id
								type
								value {
									booleanValue
									dateValue
									numberValue
									stringValue
									arrayValue {
										stringValue
										booleanValue
										dateValue
										numberValue
										objectValue {
											key
											value {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
										arrayValue {
											stringValue
											booleanValue
											dateValue
											numberValue
											arrayValue {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
									}
								}
							}
						}
					}
				}
			`
			const objectQuery = gql`
				query objects($where: objectWhereInput) {
					objects(where: $where) {
						list {
							id
							name
							internalCode
							photoPath
							source
							typeId
							contacts {
								id
								type
								value
							}
							addresses {
								fields2 {
									type
									value
								}
								id
								fields
								type
							}
							identities {
								id
								type
								value
							}
							facts {
								id
								type
								value {
									booleanValue
									dateValue
									numberValue
									stringValue
									arrayValue {
										stringValue
										booleanValue
										dateValue
										numberValue
										objectValue {
											key
											value {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
										arrayValue {
											stringValue
											booleanValue
											dateValue
											numberValue
											arrayValue {
												stringValue
												booleanValue
												dateValue
												numberValue
											}
										}
									}
								}
							}
						}
					}
				}
			`
			const variables = {
				where: {
					id: entityId
				}
			}
			const res = await api({
				query:
					entityType === "individuals"
						? individualQuery
						: entityType === "companies"
						? companyQuery
						: objectQuery,
				variables
			})
			if (res?.data?.[entityType]?.list) {
				return res?.data?.[entityType]?.list[0]
			}
		},

		async fetchPolicy() {
			this.isLoading = true
			const query = gql`
				query policies($where: policyWhereInput) {
					policies(where: $where) {
						list {
							id
							source
							issuerNumber
							cancellationReason
							isPremiumOverriden
							clauses {
								id
								order
								htmlOverride
								template {
									id
									name
									graphQlQuery
									... on clauseHtml {
										html
									}
								}
								renderedHtmlResult {
									value
								}
								renderParameters {
									contentJsonString
									variablesJsonString
								}
							}
							benefitOptions {
								key
								name
								formattedValue
								benefitTypeId
								value {
									...nestedScalarValues
								}
							}
							benefitSummary {
								benefitGraphs {
									name
									...benefitGraphFields
									options {
										optionKey
										name
										value
										detailedValue
										children {
											...benefitGraphFields
											children {
												...benefitGraphFields
												children {
													...benefitGraphFields
													children {
														...benefitGraphFields
													}
												}
											}
										}
									}
								}
							}
							facts {
								id
								type
								value {
									...nestedScalarValues
								}
							}
							endorsements {
								id
								status
								isApproved
								isRejected
								addClauseCommands {
									timestamp
									addedBy {
										...nameOfCreator
									}
									clauseId
									commandId
									order
									renderParameters {
										contentJsonString
										name
										variablesJsonString
									}
									renderedHtmlResult {
										errors
										status
										value
									}
									templateId
								}
								addContractHolderAddressCommands {
									...endorsementAddedAddressFields
								}
								addContractHolderContactCommands {
									...endorsementAddedContactFields
								}
								addContractHolderFactCommands {
									...endorsementAddedFactsFields
								}
								addContractHolderIdentityCommands {
									...endorsementAddedIdentityFields
								}
								addContractInsuredCommands {
									...endorsementAddedEntityFields
								}
								addContractInsuredAddressCommands {
									...endorsementAddedAddressFields
								}
								addContractInsuredContactCommands {
									...endorsementAddedContactFields
								}
								addContractInsuredFactCommands {
									...endorsementAddedFactsFields
								}
								addContractInsuredIdentityCommands {
									...endorsementAddedIdentityFields
								}
								addDiscountCommands {
									timestamp
									addedBy {
										...nameOfCreator
									}
									calculationJsonLogic
									commandId
									order
								}
								addOtherContractHolderAddressCommands {
									...endorsementAddedAddressFields
								}
								addOtherContractHolderContactCommands {
									...endorsementAddedContactFields
								}
								addOtherContractHolderFactCommands {
									...endorsementAddedFactsFields
								}
								addOtherContractHolderIdentityCommands {
									...endorsementAddedIdentityFields
								}
								addOtherContractHolderCommands {
									...endorsementAddedEntityFields
								}
								addPolicyFactCommands {
									...endorsementAddedFactsFields
								}
								addAssociatedContractCommands {
									commandId
									addedBy {
										...nameOfCreator
									}
									timestamp
									contract {
										facts {
											type
											value {
												...nestedScalarValues
											}
										}
									}
								}
								addLoadingCommands {
									timestamp
									addedBy {
										...nameOfCreator
									}
									calculationJsonLogic
									flat
									ratio
									commandId
									order
								}
								updateContractHolderAddressCommands {
									...endorsementChangedAddressFields
								}
								updateContractHolderContactCommands {
									...endorsementChangedContactFields
								}
								updateContractHolderFactCommands {
									...endorsementChangedFactsFields
								}
								updateContractHolderIdentityCommands {
									...endorsementChangedIdentityFields
								}
								updateContractInsuredAddressCommands {
									...endorsementChangedAddressFields
								}
								updateContractInsuredCompanyCommands {
									...endorsementChangedCompanyFields
								}
								updateContractInsuredContactCommands {
									...endorsementChangedContactFields
								}
								updateContractInsuredFactCommands {
									...endorsementChangedFactsFields
								}
								updateContractInsuredIdentityCommands {
									...endorsementChangedIdentityFields
								}
								updateContractInsuredIndividualCommands {
									...endorsementChangedIndividualFields
								}
								updateContractInsuredObjectCommands {
									timestamp
									modifiedBy {
										...nameOfCreator
									}
									commandId
									isInternalCodeChanged
									internalCode
									isNameFormatChanged
									nameFormat
									isSourceChanged
									source
									isTypeIdChanged
									typeId
								}
								updateDiscountCommands {
									timestamp
									modifiedBy {
										...nameOfCreator
									}
									commandId
									isCalculationJsonLogicChanged
									calculationJsonLogic
									isOrderChanged
									order
								}
								updateOtherContractHolderAddressCommands {
									...endorsementChangedAddressFields
								}
								updateOtherContractHolderCompanyCommands {
									...endorsementChangedCompanyFields
								}
								updateOtherContractHolderContactCommands {
									...endorsementChangedContactFields
								}
								updateOtherContractHolderFactCommands {
									...endorsementChangedFactsFields
								}
								updateOtherContractHolderIdentityCommands {
									...endorsementChangedIdentityFields
								}
								updateOtherContractHolderIndividualCommands {
									...endorsementChangedIndividualFields
								}
								updatePolicyCommands {
									timestamp
									modifiedBy {
										...nameOfCreator
									}
									contractHolderIndividual {
										...endorsementChangedIndividualFields
									}
									isContractHolderIndividualChanged
									contractHolderCompany {
										...endorsementChangedCompanyFields
									}
									isContractHolderCompanyChanged
									commandId
									isDescriptionChanged
									description
									isEndDateChanged
									endDate
									isHolderIdChanged
									holderId
									isInsuredIdsChanged
									insuredIds
									isIssueDateChanged
									issueDate
									isIssuerNumberChanged
									issuerNumber
									isPremiumChanged
									premium {
										isAmountChanged
										amount
										isCurrencyCodeChanged
										currencyCode
										isDiscountCodesChanged
										discountCodes
										isGrossAmountChanged
										grossAmount
									}
									isProductIdChanged
									productId {
										isPlanChanged
										plan
										isTypeChanged
										type
										isVersionChanged
										version
									}
									values {
										key
										value {
											arrayValue {
												stringValue
												numberValue
												booleanValue
												objectValue {
													key
													value {
														stringValue
														numberValue
														booleanValue
														objectValue {
															key
															value {
																numberValue
																stringValue
																numberValue
																booleanValue
															}
														}
													}
												}
											}
										}
									}
									isReferralCodeChanged
									referralCode
									isSourceChanged
									source
									isStartDateChanged
									startDate
									isValuesChanged
									values {
										key
										value {
											dateValue
											booleanValue
											numberValue
											stringValue
										}
									}
								}
								updatePolicyFactCommands {
									...endorsementChangedFactsFields
								}
								upsertBenefitOptionCommands {
									key
									upsertedBy {
										...nameOfCreator
									}
									typeId
									commandId
									timestamp
									value {
										...nestedScalarValues
									}
								}
								removeContractInsuredCommands {
									...endorsementRemovedFields
								}
								removeContractHolderAddressCommands {
									...endorsementRemovedFields
								}
								removeContractHolderContactCommands {
									...endorsementRemovedFields
								}
								removeContractHolderFactCommands {
									...endorsementRemovedFields
								}
								removeContractHolderIdentityCommands {
									...endorsementRemovedFields
								}
								removeContractInsuredAddressCommands {
									...endorsementRemovedEntityPrimitiveFields
								}
								removeContractInsuredContactCommands {
									...endorsementRemovedEntityPrimitiveFields
								}
								removeContractInsuredFactCommands {
									...endorsementRemovedEntityPrimitiveFields
								}
								removeContractInsuredIdentityCommands {
									...endorsementRemovedEntityPrimitiveFields
								}
								removeDiscountCommands {
									...endorsementRemovedFields
								}
								removeOtherContractHolderAddressCommands {
									...endorsementRemovedEntityPrimitiveFields
								}
								removeOtherContractHolderContactCommands {
									...endorsementRemovedEntityPrimitiveFields
								}
								removeOtherContractHolderFactCommands {
									...endorsementRemovedEntityPrimitiveFields
								}
								removeOtherContractHolderIdentityCommands {
									...endorsementRemovedEntityPrimitiveFields
								}
								removeOtherContractHolderCommands {
									...endorsementRemovedFields
								}
								removePolicyFactCommands {
									...endorsementRemovedFields
								}
								removeAssociatedContractCommands {
									commandId
									timestamp
									id
									removedBy {
										...nameOfCreator
									}
								}
								removeBenefitOptionCommands {
									commandId
									timestamp
									removedBy {
										...nameOfCreator
									}
									typeId
								}
								removeLoadingCommands {
									...endorsementRemovedFields
								}
							}
							premium {
								amount
								currencyCode
								formattedPrice
								originalPrice
								formattedOriginalPrice
								loadings {
									id
									order
									codeName {
										code
										name
									}
									formattedOriginalPrice
									ratio
									flat
								}
								appliedDiscounts {
									id
									code
									name
									ratio
									flat
									formattedFlat
									order
									formattedOriginalPrice
									originalPrice
								}
								discountCodes
								appliedTaxes {
									code
									name
									ratio
									formattedFlat
									order
									formattedOriginalPrice
								}
							}
							status
							startDate
							endDate
							issueDate
							description
							product {
								productId {
									plan
									type
									version
								}
								name
								insurer {
									id
									name
								}
								benefitGraph {
									...benefitGraphFields
									options {
										...benefitGraphFields
									}
									children {
										...benefitGraphFields
										options {
											...benefitGraphFields
										}
										children {
											...benefitGraphFields
											options {
												...benefitGraphFields
											}
										}
									}
									options {
										...benefitGraphFields
									}
								}
								benefits(typeIds: [], includeOptionals: true) {
									typeId
									value
								}
							}
							associatedContracts {
								contract {
									facts {
										id
										type
										value {
											booleanValue
											numberValue
											stringValue
											dateValue
										}
									}
									id
								}
								id
							}
							values {
								key
								value {
									arrayValue {
										stringValue
										numberValue
										booleanValue
										objectValue {
											key
											value {
												stringValue
												numberValue
												booleanValue
												objectValue {
													key
													value {
														numberValue
														stringValue
														numberValue
														booleanValue
													}
												}
											}
										}
									}
								}
							}
							referrer {
								name
							}

							contractHolder {
								... on individual {
									...queryFieldsOnIndividual
								}
								... on company {
									...queryFieldsOnCompany
								}
							}

							contractInsured {
								... on individual {
									...queryFieldsOnIndividual
								}
								... on company {
									...queryFieldsOnCompany
								}
								... on object {
									...queryFieldsOnObject
								}
							}
							otherContractHolders {
								... on individual {
									...queryFieldsOnIndividual
								}
								... on company {
									...queryFieldsOnCompany
								}
								... on object {
									...queryFieldsOnObject
								}
							}
						}
					}
				}

				fragment benefitGraphFields on benefitGraph {
					name
					optionKey
					typeId
					description
					isOptional
					isValueInput
					formattedValue: value
					value: value2 {
						...nestedScalarValues
					}
				}

				fragment queryFieldsOnObject on object {
					__typename
					id
					name
					internalCode
					photoPath
					source
					typeId
					contacts {
						id
						type
						value
					}
					addresses {
						fields2 {
							type
							value
						}
						id
						fields
						type
					}
					identities {
						id
						type
						value
					}
					facts {
						id
						type
						value {
							...nestedScalarValues
						}
					}
				}

				fragment queryFieldsOnCompany on company {
					__typename
					id
					name
					registrationNumber
					natureOfBusiness
					acceptsMarketing
					photoPath
					source
					contacts {
						id
						type
						value
					}
					addresses {
						fields2 {
							type
							value
						}
						id
						fields
						type
					}
					identities {
						id
						type
						value
					}
					facts {
						id
						type
						value {
							...nestedScalarValues
						}
					}
				}

				fragment queryFieldsOnIndividual on individual {
					__typename
					id
					name
					englishFirstName
					englishLastName
					chineseFirstName
					chineseLastName
					dateOfBirth
					gender
					occupation
					maritalStatus
					employmentStatus
					salutation
					preferredCommunicationChannel
					preferredLanguage
					countryOfResidency
					incomeRange {
						exclusiveMaximum
						exclusiveMinimum
						minimum
						maximum
					}
					acceptsMarketing
					source
					internalCode
					contacts {
						id
						type
						value
					}
					identities {
						id
						type
						value
					}
					addresses {
						fields2 {
							type
							value
						}
						id
						fields
						type
					}
					facts {
						id
						type
						value {
							...nestedScalarValues
						}
					}
				}

				fragment nameOfCreator on login {
					username
					email
					associatedUser {
						name
					}
				}

				fragment endorsementChangedIndividualFields on updateContractIndividualCommand {
					timestamp
					commandId
					modifiedBy {
						...nameOfCreator
					}
					isAcceptsMarketingChanged
					acceptsMarketing
					isPhotoPathChanged
					photoPath
					isChineseFirstNameChanged
					chineseFirstName
					isChineseLastNameChanged
					chineseLastName
					isCountryOfResidencyChanged
					countryOfResidency
					isDateOfBirthChanged
					dateOfBirth
					isEmploymentStatusChanged
					employmentStatus
					isEnglishFirstNameChanged
					englishFirstName
					isEnglishLastNameChanged
					englishLastName
					isGenderChanged
					gender
					isIncomeRangeChanged
					incomeRange {
						exclusiveMaximum
						exclusiveMinimum
						maximum
						minimum
					}
					isInternalCodeChanged
					internalCode
					isMaritalStatusChanged
					maritalStatus
					isNameFormatChanged
					nameFormat
					isOccupationChanged
					occupation
					isPreferredCommunicationChannelChanged
					preferredCommunicationChannel
					isPreferredLanguageChanged
					preferredLanguage
					isSalutationChanged
					salutation
					isSourceChanged
					source
				}

				fragment endorsementChangedIdentityFields on updateIdentityCommand {
					timestamp
					modifiedBy {
						...nameOfCreator
					}
					commandId
					entityId
					id
					type
					value
				}

				fragment endorsementChangedFactsFields on updateFactCommand {
					timestamp
					modifiedBy {
						...nameOfCreator
					}
					commandId
					entityId
					id
					isTypeChanged
					type
					isValueChanged
					value {
						...nestedScalarValues
					}
				}

				fragment endorsementChangedContactFields on updateContactCommand {
					timestamp
					modifiedBy {
						...nameOfCreator
					}
					commandId
					entityId
					type
					value
				}

				fragment endorsementChangedCompanyFields on updateContractCompanyCommand {
					timestamp
					modifiedBy {
						...nameOfCreator
					}
					commandId
					isAcceptsMarketingChanged
					acceptsMarketing
					isInternalCodeChanged
					internalCode
					isNameFormatChanged
					nameFormat
					isNatureOfBusinessChanged
					natureOfBusiness
					isRegistrationNumberChanged
					registrationNumber
					isSourceChanged
					source
					isPhotoPathChanged
					photoPath
				}

				fragment endorsementChangedAddressFields on updateAddressCommand {
					timestamp
					modifiedBy {
						...nameOfCreator
					}
					commandId
					entityId
					fields {
						type
						value
					}
					id
					type
				}

				fragment endorsementAddedContactFields on addContactCommand {
					timestamp
					addedBy {
						...nameOfCreator
					}
					commandId
					entityId
					type
					value
				}

				fragment endorsementAddedIdentityFields on addIdentityCommand {
					timestamp
					addedBy {
						...nameOfCreator
					}
					commandId
					entityId
					type
					value
				}

				fragment endorsementAddedAddressFields on addAddressCommand {
					timestamp
					addedBy {
						...nameOfCreator
					}
					commandId
					entityId
					fields {
						type
						value
					}
					type
				}

				fragment endorsementAddedEntityFields on addEntityCommand {
					timestamp
					addedBy {
						...nameOfCreator
					}
					commandId
					endorsementId
					entityId
				}

				fragment endorsementAddedFactsFields on AddFactCommandGraphType {
					timestamp
					addedBy {
						...nameOfCreator
					}
					commandId
					entityId
					id
					type
					value {
						...nestedScalarValues
					}
				}

				fragment endorsementRemovedFields on removeCommand {
					timestamp
					removedBy {
						...nameOfCreator
					}
					commandId
					endorsementId
					id
				}

				fragment endorsementRemovedEntityPrimitiveFields on removeEntityPrimitiveCommand {
					timestamp
					removedBy {
						...nameOfCreator
					}
					commandId
					endorsementId
					entityId
					primitiveId
				}

				fragment nestedScalarValues on scalarValue {
					arrayValue {
						arrayValue {
							booleanValue
							dateValue
							numberValue
							stringValue
						}
						booleanValue
						dateValue
						numberValue
						objectValue {
							key
							value {
								booleanValue
								dateValue
								numberValue
								stringValue
							}
						}
						stringValue
					}
					booleanValue
					dateValue
					numberValue
					objectValue {
						key
						value {
							arrayValue {
								booleanValue
								dateValue
								numberValue
								stringValue
							}
							booleanValue
							dateValue
							numberValue
							stringValue
						}
					}
					stringValue
				}
			`
			const variables = {
				where: {
					id: this.id
				}
			}
			const res = await api({ query, variables })
			if (res?.data?.policies?.list) {
				this.policy = res.data.policies?.list[0]
			}
			this.isLoading = false
		}
	},

	created() {
		this.fetchPolicy()
		console.log(this.onChangeEndorsement)
		console.log(this.onSavePricing)
	}
}
</script>
